import { css } from '@emotion/react'
import { Flow, Icon, SelectMenuItem, Text, Theme, useTheme, VFlow } from 'bold-ui'
import { SelectDownshiftMenuProps } from 'bold-ui/lib/components/Select/SelectSingle/SelectDownshiftMenu'
import { CustomSelectFieldProps, SelectField, selectFieldStatus } from 'components/form/final-form'
import { HLabel } from 'components/HLabel'
import { AntecedenteCiapSelectFieldDocument } from 'graphql/hooks.generated'
import {
  AntecedenteCiapSelectFieldQuery,
  AntecedenteCiapSelectFieldQueryVariables,
  CiapCapituloEnum,
} from 'graphql/types.generated'
import { Fragment, useMemo } from 'react'
import { emptyArrayOf } from 'util/array'
import { AntecedentesFamiliaresCiapFormModel } from 'view/atendimentos/detail/soap/antecedentes/model-antecedentes'

import { RenderGrupoSelectHeader } from '../RenderGrupoSelectHeader'
import { DEFAULT_SELECT_PAGE_PARAM, useAsyncQuerySelect } from '../useAsyncQuerySelect'

export type AntecedenteCiapSelectFieldModel = AntecedenteCiapSelectFieldQuery['ciaps']['content'][0]

export type AntecedenteCiapSelectFieldProps = CustomSelectFieldProps<AntecedenteCiapSelectFieldModel> & {
  capitulosExcluidos?: CiapCapituloEnum[]
  excluirCIAPsDAB?: boolean
  formValues?: Array<AntecedentesFamiliaresCiapFormModel>
  defaultItens: {
    itens: AntecedenteCiapSelectFieldModel[]
    ids: string[]
  }
}

const ID_GRUPO_SUGERIDOS = 'idGrupoSugeridos'
const IG_GRUPO_OUTROS_CIAPS = 'idGrupoOutrosCiaps'

export function AntecedenteCiapSelectField(props: AntecedenteCiapSelectFieldProps) {
  const { capitulosExcluidos, excluirCIAPsDAB, formValues, defaultItens, itemIsDisabled, ...rest } = props
  const theme = useTheme()
  const styles = useMemo(() => createStyles(theme), [theme])

  const {
    skipping,
    selectProps: { items, ...asyncProps },
  } = useAsyncQuerySelect<
    AntecedenteCiapSelectFieldModel,
    AntecedenteCiapSelectFieldQuery,
    AntecedenteCiapSelectFieldQueryVariables
  >({
    query: AntecedenteCiapSelectFieldDocument,
    extractItems: (data) => data?.ciaps?.content,
    variables: (inputQuery: string): AntecedenteCiapSelectFieldQueryVariables => ({
      input: {
        query: inputQuery,
        pageParams: DEFAULT_SELECT_PAGE_PARAM,
        capitulosExcluidos,
        excluirCIAPsDAB,
      },
    }),
    skip: (inputString) => inputString?.trim()?.length < 2,
    debounceTime: 500,
  })

  const selectItems = useMemo<{
    hasOutrosCiaps: boolean
    items: AntecedenteCiapSelectFieldModel[]
  }>(() => {
    const sortedItems = (items ?? emptyArrayOf<AntecedenteCiapSelectFieldModel>()).toSorted((a, b) => {
      return a.descricao.localeCompare(b.descricao)
    })

    const ciapsSugeridos: AntecedenteCiapSelectFieldModel[] = [
      { id: ID_GRUPO_SUGERIDOS, codigo: '', descricao: '' },
    ].concat(defaultItens.itens)

    const outrosCiaps: AntecedenteCiapSelectFieldModel[] = skipping
      ? []
      : [{ id: IG_GRUPO_OUTROS_CIAPS, codigo: '', descricao: '' }]

    // Não duplicar itens na combo com os defaultSugeridos
    sortedItems?.forEach((item) => {
      if (!defaultItens.ids.includes(item.id)) outrosCiaps.push(item)
    })

    return {
      hasOutrosCiaps: outrosCiaps.length > 1,
      items: outrosCiaps.concat(ciapsSugeridos),
    }
  }, [items, defaultItens.itens, defaultItens.ids, skipping])

  const itemToString = (item: AntecedenteCiapSelectFieldModel) => item && `${item.descricao} - ${item.codigo}`

  const renderItem = (item: AntecedenteCiapSelectFieldModel) => {
    return (
      <VFlow vSpacing={0} data-testid='AntecedenteCiapSelectField.item'>
        <Text fontWeight='bold'>{item.descricao}</Text>
        <HLabel title='Código '>{item.codigo}</HLabel>
      </VFlow>
    )
  }

  const Item = (
    itemProps: SelectDownshiftMenuProps<AntecedenteCiapSelectFieldModel> & {
      item: AntecedenteCiapSelectFieldModel
      index: number
    }
  ) => {
    const {
      downshift: { getItemProps, highlightedIndex },
      index,
      item,
      renderItem,
    } = itemProps

    const renderGrupoCiapsSugeridos = itemProps.item?.id === ID_GRUPO_SUGERIDOS
    const renderGrupoOutrosCiaps = itemProps.item?.id === IG_GRUPO_OUTROS_CIAPS

    if (renderGrupoOutrosCiaps && skipping) return <Fragment></Fragment>

    if (renderGrupoOutrosCiaps) {
      return (
        <RenderGrupoSelectHeader
          titulo='Outras CIAP 2'
          emptyMessage={!selectItems.hasOutrosCiaps && 'Nenhuma outra CIAP 2.'}
          itemProps={getItemProps({ item, disabled: true })}
        />
      )
    }

    if (renderGrupoCiapsSugeridos) {
      return (
        <RenderGrupoSelectHeader
          titulo='Opções rápidas para CIAP 2'
          itemProps={getItemProps({ item, disabled: true })}
        />
      )
    }

    const isDisabled = itemIsDisabled(itemProps.item)

    return (
      <SelectMenuItem
        selected={highlightedIndex === index}
        style={isDisabled ? styles.disabledMenuItem : undefined}
        {...(isDisabled ? getItemProps({ item, disabled: true }) : getItemProps({ item }))}
      >
        <Flow direction='horizontal' alignItems='center' justifyContent='space-between'>
          <div css={isDisabled ? styles.disabledItemContent : undefined}>{renderItem(item)}</div>

          {isDisabled && (
            <Flow direction='horizontal' alignItems='center' gap={0.2} style={styles.selectedItemLabel}>
              <Icon icon={selectFieldStatus.SUCCESS.icon} size={1} style={styles.selectedItemIcon} />
              {'Adicionado'}
            </Flow>
          )}
        </Flow>
      </SelectMenuItem>
    )
  }

  return (
    <SelectField<AntecedenteCiapSelectFieldModel>
      placeholder='Pesquise ou selecione um CIAP 2'
      itemToString={itemToString}
      items={selectItems.items}
      renderItem={renderItem}
      components={{
        Item,
      }}
      {...asyncProps}
      {...rest}
    />
  )
}

const createStyles = (theme: Theme) => ({
  disabledMenuItem: css`
    cursor: not-allowed;

    &:hover {
      background: ${theme.pallete.primary.c100};
    }
  `,
  disabledItemContent: css`
    opacity: 0.5;
  `,
  selectedItemLabel: css`
    color: ${selectFieldStatus.SUCCESS.color};
  `,
  selectedItemIcon: css`
    display: block;
  `,
})
